/**
 * A aplicação.
 *
 * A navegação é montada a partir de `/recursos` da API, e não escrita à mão.
 * O motivo é o mesmo que fez a tela de cadastro ser uma só: uma lista de
 * destinos mantida em dois lugares diverge, e divergir é como se chega às 179
 * telas do concorrente.
 *
 * Sem API no ar, o app cai na página de fundação. Cair num estado útil em vez
 * de numa tela de erro importa numa ferramenta cujo usuário volta depois de
 * um ano e não sabe se o problema é dele.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  api,
  pedir,
  sessao,
  type Ajuda as TextoDeAjuda,
  type Grupo,
  type ProjetoDoUsuario,
  type Recurso,
  type Usuario,
} from './api'
import { AppShell, type Destino } from './components/AppShell'
import { Cadastro } from './telas/Cadastro'
import { Comparar } from './telas/Comparar'
import { Custo } from './telas/Custo'
import { AJUDA_DAS_TAREFAS } from './ajudaDasTarefas'
import { Entrar, EscolherProjeto } from './telas/Entrar'
import { Escola } from './telas/Escola'
import { Grade } from './telas/Grade'
import { Pendencias } from './telas/Pendencias'
import { AjustarMenu } from './telas/AjustarMenu'
import { Atribuir } from './telas/Atribuir'
import { Importar } from './telas/Importar'
import { Formularios } from './telas/Formularios'
import { Disponibilidade } from './telas/Disponibilidade'
import { Ajuda } from './telas/Ajuda'
import { Tour } from './components/Tour'
import { Fundacao } from './telas/Fundacao'

/** A última escola aberta. Conveniência por navegador, nada mais. */
const ULTIMO_PROJETO = 'pauta:projeto'

/**
 * Caminho do recurso → ícone. Um por destino, sem repetir.
 *
 * Repetir era o defeito: a mesma casa em Unidades, Locais e Deslocamentos, a
 * mesma etiqueta em cinco linhas. Ícone repetido não distingue — ensina a
 * pessoa a ler pelo texto e a ignorar a coluna.
 */
const ICONE: Record<string, Destino['icone']> = {
  unidades: 'unidades',
  locais: 'locais',
  turnos: 'turnos',
  esquemas: 'esquemas',
  deslocamentos: 'deslocamentos',
  cursos: 'cursos',
  turmas: 'turmas',
  areas: 'areas',
  disciplinas: 'disciplinas',
  professores: 'professores',
  presets: 'presets',
  ofertas: 'ofertas',
  preferencias: 'preferencias',
  'horas-atividade': 'horasAtividade',
  jornadas: 'jornadas',
  coincidencias: 'coincidencias',
  atribuicoes: 'atribuicoes',
  regimes: 'regimes',
  trilhas: 'trilhas',
}

const ROTULO_GRUPO: Record<Grupo, string> = {
  ESTRUTURA: 'Estrutura',
  ACADEMICO: 'Dados',
  AGENDAVEL: 'Grade curricular',
}

export default function App() {
  const [recursos, setRecursos] = useState<Recurso[]>([])
  // `?tela=fundacao` abre a fundação. Ela saiu do menu de propósito: é o
  // especímen do sistema de design e um medidor do contrato gerado —
  // ferramenta de quem constrói, não tela de quem monta horário. Estava na
  // navegação só porque nasceu quando não havia mais nada para mostrar.
  const [atual, setAtual] = useState(
    () => new URLSearchParams(location.search).get('tela') ?? 'grade',
  )
  // De onde a pessoa veio, para a Ajuda abrir no verbete da tela dela. Não
  // é histórico de navegação: só o passo anterior, que é o que a ajuda de
  // contexto precisa saber.
  const [anterior, setAnterior] = useState<string | undefined>(undefined)
  const ir = useCallback(
    (destino: string) =>
      setAtual((atual) => {
        if (destino !== atual) setAnterior(atual)
        return destino
      }),
    [],
  )
  /**
   * O tour em andamento, se houver.
   *
   * Mora no App porque ele atravessa a navegação: a pessoa está na Ajuda,
   * clica em "Mostrar na tela", e o tour precisa sobreviver à troca de tela
   * para apontar para os campos da tela de destino.
   */
  const [tour, setTour] = useState<TextoDeAjuda['tour']>(undefined)
  const [offline, setOffline] = useState(false)
  // Muda quando a pessoa ajusta o menu, para a navegação se remontar
  // sem recarregar a página.
  const [versaoDoMenu, setVersaoDoMenu] = useState(0)

  // `null` é "ainda não sei"; a tela de entrada só aparece depois da resposta,
  // senão ela pisca na cara de quem já estava logado.
  const [usuario, setUsuario] = useState<Usuario | null>(null)
  const [projetos, setProjetos] = useState<ProjetoDoUsuario[]>([])
  const [projeto, setProjeto] = useState<string | null>(null)
  const [conferido, setConferido] = useState(false)

  const carregarSessao = useCallback(async () => {
    try {
      const { usuario: eu, projetos: meus } = await sessao.eu()
      setUsuario(eu)
      setProjetos(meus)
      const lembrado = localStorage.getItem(ULTIMO_PROJETO)
      const valido = meus.find((p) => p.id === lembrado)?.id
      // Com uma escola só, escolher é confirmar o óbvio.
      setProjeto(valido ?? (meus.length === 1 ? meus[0]!.id : null))
    } catch {
      setUsuario(null)
    } finally {
      setConferido(true)
    }
  }, [])

  useEffect(() => {
    void carregarSessao()
  }, [carregarSessao])

  useEffect(() => {
    if (!projeto) return
    localStorage.setItem(ULTIMO_PROJETO, projeto)
    api
      .recursosDoProjeto(projeto)
      .then(setRecursos)
      .catch(() => setOffline(true))
  }, [projeto, versaoDoMenu])

  const grupos = useMemo(() => {
    const porGrupo = new Map<string, Destino[]>([
      [
        'Horário',
        [
          {
            id: 'grade',
            rotulo: 'Grade',
            icone: 'grade' as const,
            ajuda: AJUDA_DAS_TAREFAS.grade!,
          },
          {
            id: 'comparar',
            rotulo: 'Comparar cenários',
            icone: 'comparar' as const,
            ajuda: AJUDA_DAS_TAREFAS.comparar!,
          },
          // Fica em Horário e não no cadastro porque não é atributo de
          // ninguém: é leitura sobre o cenário inteiro. O custo de UMA
          // pessoa mora na ficha dela, em Professores. "Custo", e não "Custo
          // da grade": sem grade montada o número é o do planejado, e o nome
          // antigo fazia parecer que o sistema o tinha produzido (§23.4).
          {
            id: 'custo',
            rotulo: 'Custo',
            icone: 'custo' as const,
            ajuda: AJUDA_DAS_TAREFAS.custo!,
          },
          // O que não fecha, com dono e histórico (§24.4). Em Horário porque
          // é o trabalho de fechar a grade, e não cadastro de ninguém.
          {
            id: 'pendencias',
            rotulo: 'Pendências',
            icone: 'pendencia' as const,
            ajuda: AJUDA_DAS_TAREFAS.pendencias!,
          },
        ],
      ],
    ])
    for (const r of recursos) {
      // `ativo === false` é o que a escola escondeu. A lista chega inteira
      // porque a tela de ajuste precisa dela; a navegação usa só o que ficou.
      if (r.ativo === false) continue
      const rotulo = ROTULO_GRUPO[r.grupo]
      const lista = porGrupo.get(rotulo) ?? []
      lista.push({
        id: r.caminho,
        rotulo: r.rotulo,
        icone: ICONE[r.caminho] ?? 'disciplinas',
        // O manual deste recurso vem do catálogo, junto com o rótulo e os
        // campos. Mesma lista, mesma viagem.
        ajuda: r.ajuda,
      })
      porGrupo.set(rotulo, lista)
    }

    // Importar não é uma entidade, é a tarefa que substitui uma semana de
    // digitação — e por isso tem entrada própria em vez de virar um botão
    // escondido dentro de Professores. Quando §6.11 reagrupar o cadastro,
    // o lugar natural dela passa a ser ao lado de Preferências.
    if (recursos.length > 0) {
      const dados = porGrupo.get(ROTULO_GRUPO.ACADEMICO) ?? []
      dados.push({
        id: 'importar',
        rotulo: 'Importar disponibilidade',
        icone: 'importar',
        ajuda: AJUDA_DAS_TAREFAS.importar!,
      })
      // O caminho de volta da planilha: em vez de recolher e redigitar, cada
      // professor preenche o dele, já sabendo o que precisa cobrir (P12).
      dados.push({
        id: 'formularios',
        rotulo: 'Formulário de disponibilidade',
        icone: 'formulario',
        ajuda: AJUDA_DAS_TAREFAS.formularios!,
      })
      porGrupo.set(ROTULO_GRUPO.ACADEMICO, dados)
    }

    return [...porGrupo].map(([rotulo, itens]) => ({ rotulo, itens }))
  }, [recursos])

  /**
   * O menu, mais as telas que o manual precisa cobrir e o menu não mostra.
   *
   * Ajustar o menu mora no rodapé da navegação, não na lista — mas é uma tela
   * que alguém opera, e um manual que a omite deixa a pessoa sem saber que
   * desmarcar não apaga nada, que é justamente a dúvida que a trava.
   */
  const paraOManual = useMemo(() => {
    if (grupos.length <= 1) return grupos
    return [
      ...grupos,
      {
        rotulo: 'Ajustes',
        itens: [
          {
            id: 'escola',
            rotulo: 'A escola',
            icone: 'unidades' as const,
            ajuda: AJUDA_DAS_TAREFAS.escola!,
          },
          {
            id: 'menu',
            rotulo: 'O que aparece no menu',
            icone: 'ajustar' as const,
            ajuda: AJUDA_DAS_TAREFAS.menu!,
          },
        ],
      },
    ]
  }, [grupos])

  const recurso = recursos.find((r) => r.caminho === atual)

  if (!conferido) return null
  if (!usuario) return <Entrar aoEntrar={() => void carregarSessao()} />

  if (!projeto) {
    return (
      <EscolherProjeto
        projetos={projetos}
        aoEscolher={setProjeto}
        aoCriar={async (nome, ano_letivo) => {
          const criado = await pedir<{ id: string }>('/projetos', {
            method: 'POST',
            body: JSON.stringify({ nome, ano_letivo }),
          })
          await carregarSessao()
          setProjeto(criado.id)
        }}
      />
    )
  }

  return (
    <AppShell
      grupos={grupos}
      atual={atual}
      onIr={ir}
      offline={offline}
      usuario={usuario}
      ocultos={recursos.filter((r) => r.ativo === false).length}
      projeto={projetos.find((p) => p.id === projeto)}
      aoAbrirEscola={() => ir('escola')}
      podeTrocarProjeto={projetos.length > 1}
      aoTrocarProjeto={() => setProjeto(null)}
      aoSair={async () => {
        await sessao.sair()
        setUsuario(null)
        setProjeto(null)
      }}
    >
      {/* A atribuição tem recurso no catálogo — é o que lhe dá entrada no menu
          e CRUD genérico — mas não usa a tela genérica: a tarefa é uma frase
          ("Ana leva duas, Bruno três"), e o cadastro a quebraria em três
          telas. Por isso ela vem ANTES do `recurso`. */}
      {atual === 'ajuda' ? (
        // `anterior` é a tela de onde a pessoa veio: é o que faz o manual
        // abrir no verbete dela em vez de no começo.
        <Ajuda
          grupos={paraOManual}
          foco={anterior}
          onIr={ir}
          aoMostrar={(id) => {
            const destino = paraOManual.flatMap((g) => g.itens).find((d) => d.id === id)
            ir(id)
            setTour(destino?.ajuda.tour)
          }}
        />
      ) : atual === 'escola' ? (
        <Escola
          projeto={projeto}
          aoRenomear={() => void carregarSessao()}
          aoExcluir={async () => {
            // A escola deixou de existir: recarregar a sessão é o que tira o
            // nome da lista, e soltar o projeto é o que impede a tela de
            // seguir pedindo dados de algo que não está mais lá.
            setProjeto(null)
            await carregarSessao()
          }}
        />
      ) : atual === 'menu' ? (
        <AjustarMenu projeto={projeto} aoMudar={() => setVersaoDoMenu((v) => v + 1)} />
      ) : atual === 'atribuicoes' ? (
        <Atribuir projeto={projeto} />
      ) : recurso && recurso.caminho === 'preferencias' ? (
        // A disponibilidade como semana por professor; a tabela crua de
        // `preferencia` vira a aba "Lista completa", para o que não é
        // professor e para o "prefere / prefere não".
        <Disponibilidade
          key="preferencias"
          projeto={projeto}
          listaCompleta={<Cadastro projeto={projeto} recurso={recurso} recursos={recursos} />}
        />
      ) : recurso ? (
        <Cadastro
          // **A chave não é otimização, é correção.** Sem ela o React
          // reaproveita a instância ao trocar de recurso, e por um quadro a
          // tela de Turmas renderiza com o registro de Esquemas ainda aberto
          // — tempo suficiente para o bloco de relações pedir "as relações da
          // turma <id do esquema>" e levar 404. Com a chave, o estado nasce
          // limpo junto com o recurso.
          key={recurso.caminho}
          projeto={projeto}
          recurso={recurso}
          recursos={recursos}
          abrirFormulario={!!tour}
        />
      ) : atual === 'comparar' ? (
        <Comparar projeto={projeto} />
      ) : atual === 'custo' ? (
        <Custo projeto={projeto} />
      ) : atual === 'pendencias' ? (
        <Pendencias projeto={projeto} />
      ) : atual === 'importar' ? (
        <Importar projeto={projeto} />
      ) : atual === 'formularios' ? (
        <Formularios projeto={projeto} />
      ) : atual === 'fundacao' ? (
        <Fundacao />
      ) : (
        <Grade projeto={projeto} />
      )}

      {/* Fica por último e por cima de tudo, inclusive do menu: o tour
          aponta para a tela, e sair dela no meio do passo o deixaria
          falando de um campo que não está mais lá. */}
      {tour && tour.length > 0 && (
        <Tour passos={tour} aoFechar={() => setTour(undefined)} />
      )}
    </AppShell>
  )
}
